import {
  isDesyncJob,
  isForwardJob,
  isJobActive,
  jobProviderLabel,
  type SyncJob,
} from '@/api/queries/syncJobs';
import { Icon } from '@/components/ui';

function jobLabel(job: SyncJob): string {
  if (job.status === 'queued') return 'Queued…';
  if (isForwardJob(job.type)) return 'Auto-syncing…';
  if (job.total > 0) return `${job.processed.toLocaleString()} / ${job.total.toLocaleString()}`;
  return isDesyncJob(job.type) ? 'Removing…' : 'Syncing…';
}

function jobKind(job: SyncJob): string {
  if (isForwardJob(job.type)) return 'auto-sync';
  if (isDesyncJob(job.type)) return 'removal';
  return 'sync';
}

export function SyncJobProgress({ jobs }: { jobs: SyncJob[] }) {
  const active = jobs.filter((j) => isJobActive(j.status));
  if (active.length === 0) return null;

  const total = active.reduce((sum, j) => sum + (j.total ?? 0), 0);
  const processed = active.reduce((sum, j) => sum + (j.processed ?? 0), 0);
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const label = active.length === 1 ? jobLabel(active[0]) : `${active.length} syncs running…`;
  const allForward = active.every((j) => isForwardJob(j.type));
  const title = active
    .map((j) => `${jobProviderLabel(j.type)} ${jobKind(j)} — ${j.status}`)
    .join('\n');

  return (
    <div
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 6,
        padding: '4px 8px',
        background: 'var(--brand-soft, #eef2ff)',
        color: 'var(--brand, #4f46e5)',
        borderRadius: 'var(--radius-sm, 6px)',
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name={allForward ? 'refresh' : 'send'} size={11} />
      <span>{label}</span>
      <div
        style={{
          width: 60,
          height: 4,
          background: 'rgba(0,0,0,0.08)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'currentColor',
            transition: 'width 240ms ease-out',
          }}
        />
      </div>
    </div>
  );
}
